import SEOHelmet from "@/components/SEOHelmet";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveContainer from "@/components/layout/ResponsiveContainer";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Card } from "@/components/ui/card";
import { ExternalLink, Shield, Users, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const UeberUns = () => {
  return (
    <>
      <SEOHelmet />

      <Navigation />
      <PageBreadcrumbs items={[
        { label: "Home", path: "/" },
        { label: "Über uns" }
      ]} />

      <main className="relative min-h-screen pb-20">
        {/* Hero Section */}
        <section className="pt-24 pb-16">
          <ResponsiveContainer size="lg">
            <div className="text-center space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-accent via-accent/80 to-primary bg-clip-text text-transparent">
                Über uns
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
                Ihr kompetenter Partner für Cyberversicherungen im Gesundheitswesen
              </p>
            </div>
          </ResponsiveContainer>
        </section>

        {/* Mission Section */}
        <section className="py-12">
          <ResponsiveContainer size="lg">
            <div className="prose prose-invert max-w-none">
              <Card className="p-8 bg-card/30 backdrop-blur-sm border-accent/20">
                <div className="flex items-start gap-4 mb-6">
                  <Shield className="w-10 h-10 text-accent flex-shrink-0" />
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                      Unsere Mission
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                      praxis-versichert.de ist Ihre spezialisierte Plattform für Cyberversicherungen im medizinischen Bereich. 
                      Wir unterstützen Ärzte, Zahnärzte, Kliniken und MVZ dabei, sich optimal gegen die wachsenden Cyber-Risiken abzusichern.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mt-4">
                      Mit jahrelanger Erfahrung in der Versicherungsbranche und tiefem Verständnis für die besonderen Anforderungen 
                      des Gesundheitswesens bieten wir maßgeschneiderte Lösungen für Ihren optimalen Schutz.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </ResponsiveContainer>
        </section>

        {/* Partner Network Section */}
        <section className="py-12">
          <ResponsiveContainer size="lg">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-accent via-accent/80 to-primary bg-clip-text text-transparent">
                Unser Netzwerk
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Wir sind Teil eines starken Netzwerks von spezialisierten Versicherungsexperten
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Partner 1 */}
              <Card className="p-8 bg-card/30 backdrop-blur-sm border-accent/20 hover:border-accent/40 transition-all group">
                <div className="flex items-start gap-4">
                  <Users className="w-12 h-12 text-accent flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                      cyberversicherungen-finden.de
                      <ExternalLink className="w-4 h-4 text-accent" />
                    </h3>
                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      Ihre umfassende Vergleichsplattform für Cyberversicherungen. Hier finden Sie detaillierte 
                      Tarifvergleiche, Expertenbewertungen und individuelle Beratung für alle Branchen.
                    </p>
                    <a 
                      href="https://www.cyberversicherungen-finden.de" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
                    >
                      Zur Website
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </Card>

              {/* Partner 2 */}
              <Card className="p-8 bg-card/30 backdrop-blur-sm border-accent/20 hover:border-accent/40 transition-all group">
                <div className="flex items-start gap-4">
                  <Heart className="w-12 h-12 text-accent flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-foreground mb-3 flex items-center gap-2">
                      versicherungsmakler.team
                      <ExternalLink className="w-4 h-4 text-accent" />
                    </h3>
                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      Ihr persönliches Versicherungsmakler-Team mit ganzheitlicher Beratung. Von der 
                      Risikoanalyse bis zur optimalen Absicherung – wir sind für Sie da.
                    </p>
                    <a 
                      href="https://www.versicherungsmakler.team" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors font-medium"
                    >
                      Zur Website
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </Card>
            </div>
          </ResponsiveContainer>
        </section>

        {/* Values Section */}
        <section className="py-12">
          <ResponsiveContainer size="lg">
            <Card className="p-8 bg-card/30 backdrop-blur-sm border-accent/20">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-6 text-center">
                Unsere Werte
              </h2>
              <div className="grid sm:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-4xl mb-3">🎯</div>
                  <h3 className="font-semibold text-foreground mb-2">Spezialisierung</h3>
                  <p className="text-sm text-muted-foreground">
                    Fokus auf Cyberversicherungen für medizinische Einrichtungen
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-4xl mb-3">🤝</div>
                  <h3 className="font-semibold text-foreground mb-2">Vertrauen</h3>
                  <p className="text-sm text-muted-foreground">
                    Transparente Beratung und ehrliche Empfehlungen
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-4xl mb-3">💡</div>
                  <h3 className="font-semibold text-foreground mb-2">Expertise</h3>
                  <p className="text-sm text-muted-foreground">
                    Langjährige Erfahrung im Versicherungs- und Gesundheitswesen
                  </p>
                </div>
              </div>
            </Card>
          </ResponsiveContainer>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <ResponsiveContainer size="lg">
            <Card className="p-8 sm:p-12 bg-gradient-to-br from-accent/10 via-primary/5 to-accent/10 border-accent/30 text-center">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Bereit für optimalen Schutz?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                Lassen Sie sich jetzt kostenlos und unverbindlich von unseren Experten beraten
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-accent hover:bg-accent/90">
                  <Link to="/termin-buchen">
                    Jetzt Beratungstermin buchen
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/tarifwissen">
                    Tarife vergleichen
                  </Link>
                </Button>
              </div>
            </Card>
          </ResponsiveContainer>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default UeberUns;
